(() => {
  "use strict";

  const FUNDRAISING_BODY_CLASS =
    "oocc-fundraising-page";

  const SCROLL_LOCK_CLASS =
    "oocc-fr-scroll-locked";

  const moduleRegistry = new Map();
  const initializedModules = new WeakMap();

  const reducedMotionQuery =
    window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );


  /* ========================================================
     SHARED UTILITIES
     ======================================================== */

  function isFundraisingPage() {
    return document.body?.classList.contains(
      FUNDRAISING_BODY_CLASS
    );
  }

  function integer(value, fallback = 0) {
    const parsed = Number.parseInt(
      value,
      10
    );

    return Number.isFinite(parsed)
      ? parsed
      : fallback;
  }

  function currencyFormatter(
    currency = "USD",
    locale =
      document.documentElement.lang ||
      "en-US"
  ) {
    return new Intl.NumberFormat(
      locale,
      {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    );
  }

  function numberFormatter(
    locale =
      document.documentElement.lang ||
      "en-US"
  ) {
    return new Intl.NumberFormat(
      locale,
      {
        maximumFractionDigits: 0
      }
    );
  }

  function register(
    selector,
    initializer
  ) {
    if (
      typeof selector !== "string" ||
      typeof initializer !== "function"
    ) {
      return;
    }

    moduleRegistry.set(
      selector,
      initializer
    );
  }

  function initializeElement(
    element,
    selector,
    initializer
  ) {
    let modules =
      initializedModules.get(element);

    if (!modules) {
      modules = new Map();

      initializedModules.set(
        element,
        modules
      );
    }

    if (modules.has(selector)) {
      return;
    }

    const cleanup =
      initializer(element) || null;

    modules.set(
      selector,
      cleanup
    );
  }

  function initializeScope(
    scope = document
  ) {
    if (!isFundraisingPage()) {
      return;
    }

    moduleRegistry.forEach(
      (initializer, selector) => {
        const elements = [];

        if (
          scope instanceof Element &&
          scope.matches(selector)
        ) {
          elements.push(scope);
        }

        if (
          typeof scope.querySelectorAll ===
          "function"
        ) {
          elements.push(
            ...scope.querySelectorAll(
              selector
            )
          );
        }

        elements.forEach(
          (element) => {
            initializeElement(
              element,
              selector,
              initializer
            );
          }
        );
      }
    );
  }

  function destroyScope(scope) {
    if (!scope) {
      return;
    }

    const elements = [
      ...(scope instanceof Element
        ? [scope]
        : []),
      ...(typeof scope.querySelectorAll ===
      "function"
        ? scope.querySelectorAll("*")
        : [])
    ];

    elements.forEach((element) => {
      const modules =
        initializedModules.get(element);

      if (!modules) {
        return;
      }

      modules.forEach((cleanup) => {
        if (
          typeof cleanup ===
          "function"
        ) {
          cleanup();
        }
      });

      initializedModules.delete(
        element
      );
    });
  }


  /* ========================================================
     CAMPAIGN CALCULATOR
     ======================================================== */

  register(
    "[data-oocc-campaign-calculator]",
    (section) => {
      const rows = Array.from(
        section.querySelectorAll(
          "[data-oocc-calculator-row]"
        )
      );

      if (!rows.length) {
        return null;
      }

      const freeShippingThreshold =
        Math.max(
          1,
          integer(
            section.dataset
              .freeShippingThreshold,
            90
          )
        );

      const creditThreshold =
        Math.max(
          1,
          integer(
            section.dataset
              .creditThreshold,
            250
          )
        );

      const creditAmountCents =
        Math.max(
          0,
          integer(
            section.dataset
              .creditAmountCents,
            10000
          )
        );

      const pickupSavingsCents =
        Math.max(
          0,
          integer(
            section.dataset
              .pickupSavingsCents,
            200
          )
        );

      const quantityStep =
        Math.max(
          1,
          integer(
            section.dataset
              .quantityStep,
            5
          )
        );

      const defaultFulfillment =
        section.dataset
          .defaultFulfillment ||
        "shipping";

      const currencyCode =
        section.dataset.currency ||
        "USD";

      const locale =
        document.documentElement.lang ||
        "en-US";

      const currency =
        currencyFormatter(
          currencyCode,
          locale
        );

      const wholeDollarCurrency =
        new Intl.NumberFormat(
          locale,
          {
            style: "currency",
            currency:
              currencyCode,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }
        );

      const number =
        numberFormatter(locale);

      const fulfillmentInputs =
        Array.from(
          section.querySelectorAll(
            "[data-oocc-calculator-fulfillment]"
          )
        );

      const totalItemsOutput =
        section.querySelector(
          "[data-oocc-calculator-total-items]"
        );

      const supporterSalesOutput =
        section.querySelector(
          "[data-oocc-calculator-supporter-sales]"
        );

      const creditCountOutput =
        section.querySelector(
          "[data-oocc-calculator-credit-count]"
        );

      const productCostOutput =
        section.querySelector(
          "[data-oocc-calculator-product-cost]"
        );

      const creditTotalOutput =
        section.querySelector(
          "[data-oocc-calculator-credit-total]"
        );

      const pickupTotalOutput =
        section.querySelector(
          "[data-oocc-calculator-pickup-total]"
        );

      const shippingValueOutput =
        section.querySelector(
          "[data-oocc-calculator-shipping-value]"
        );

      const shippingDetailOutput =
        section.querySelector(
          "[data-oocc-calculator-shipping-detail]"
        );

      const shippingOptionOutput =
        section.querySelector(
          "[data-oocc-calculator-shipping-option]"
        );

      const invoiceOutput =
        section.querySelector(
          "[data-oocc-calculator-final-invoice]"
        );

      const invoiceNoteOutput =
        section.querySelector(
          "[data-oocc-calculator-invoice-note]"
        );

      const fundsRaisedOutput =
        section.querySelector(
          "[data-oocc-calculator-funds-raised]"
        );

      const fundsQualifierOutput =
        section.querySelector(
          "[data-oocc-calculator-funds-qualifier]"
        );

      const summarySentenceOutput =
        section.querySelector(
          "[data-oocc-calculator-summary-sentence]"
        );

      const clearButton =
        section.querySelector(
          "[data-oocc-calculator-clear]"
        );

      const shippingStatus =
        section.querySelector(
          "[data-oocc-calculator-shipping-status]"
        );

      const shippingStatusText =
        section.querySelector(
          "[data-oocc-calculator-shipping-status-text]"
        );

      const shippingProgress =
        section.querySelector(
          "[data-oocc-calculator-shipping-progress]"
        );

      const shippingProgressBar =
        section.querySelector(
          "[data-oocc-calculator-shipping-progress-bar]"
        );

      const shippingProgressValue =
        section.querySelector(
          "[data-oocc-calculator-shipping-progress-value]"
        );

      const creditStatus =
        section.querySelector(
          "[data-oocc-calculator-credit-status]"
        );

      const creditStatusText =
        section.querySelector(
          "[data-oocc-calculator-credit-status-text]"
        );

      const creditProgress =
        section.querySelector(
          "[data-oocc-calculator-credit-progress]"
        );

      const creditProgressBar =
        section.querySelector(
          "[data-oocc-calculator-credit-progress-bar]"
        );

      const creditProgressValue =
        section.querySelector(
          "[data-oocc-calculator-credit-progress-value]"
        );

      const listeners = [];

      function on(
        element,
        eventName,
        handler
      ) {
        if (!element) {
          return;
        }

        element.addEventListener(
          eventName,
          handler
        );

        listeners.push(() => {
          element.removeEventListener(
            eventName,
            handler
          );
        });
      }

      function formatMoney(cents) {
        return currency.format(
          Math.max(0, cents) /
            100
        );
      }

      function formatWholeDollars(
        cents
      ) {
        return wholeDollarCurrency.format(
          Math.max(0, cents) /
            100
        );
      }

      function formatDeduction(cents) {
        return `−${formatMoney(cents)}`;
      }

      function itemWord(value) {
        return value === 1
          ? "item"
          : "items";
      }

      function creditWord(value) {
        return value === 1
          ? "credit"
          : "credits";
      }

      function sanitizeQuantity(
        input
      ) {
        const value = Math.max(
          0,
          Math.round(
            Number(
              input.value || 0
            )
          )
        );

        input.value =
          String(value);

        return value;
      }

      function selectedFulfillment() {
        const selected =
          fulfillmentInputs.find(
            (input) =>
              input.checked
          );

        return selected
          ? selected.value
          : "shipping";
      }

      function setProgress(
        element,
        bar,
        value,
        maximum
      ) {
        if (!element || !bar) {
          return;
        }

        const safeMaximum =
          Math.max(1, maximum);

        const safeValue =
          Math.max(
            0,
            Math.min(
              value,
              safeMaximum
            )
          );

        const percentage =
          (safeValue /
            safeMaximum) *
          100;

        bar.style.width =
          `${percentage}%`;

        element.setAttribute(
          "aria-valuenow",
          String(safeValue)
        );

        element.setAttribute(
          "aria-valuemax",
          String(safeMaximum)
        );
      }

      function updateShippingMilestone(
        totalItems,
        fulfillment
      ) {
        if (
          !shippingStatus ||
          !shippingStatusText
        ) {
          return;
        }

        if (
          fulfillment ===
          "pickup"
        ) {
          if (totalItems === 0) {
            shippingStatus.dataset.state =
              "neutral";

            shippingStatusText.textContent =
              `Findley Lake pickup is selected. ` +
              `Enter quantities to calculate the ` +
              `${formatMoney(pickupSavingsCents)}-per-item savings.`;

            shippingProgressValue.textContent =
              "Pickup selected";

            setProgress(
              shippingProgress,
              shippingProgressBar,
              0,
              freeShippingThreshold
            );

            return;
          }

          shippingStatus.dataset.state =
            "success";

          shippingStatusText.textContent =
            `Findley Lake pickup is selected. ` +
            `Your organization saves ` +
            `${formatMoney(
              totalItems *
                pickupSavingsCents
            )} on this projected order.`;

          shippingProgressValue.textContent =
            "Pickup selected";

          setProgress(
            shippingProgress,
            shippingProgressBar,
            freeShippingThreshold,
            freeShippingThreshold
          );

          return;
        }

        if (
          totalItems >=
          freeShippingThreshold
        ) {
          shippingStatus.dataset.state =
            "success";

          shippingStatusText.textContent =
            `Free shipping is unlocked for delivery ` +
            `to one organization location.`;

          shippingProgressValue.textContent =
            `${number.format(
              totalItems
            )} items`;

          setProgress(
            shippingProgress,
            shippingProgressBar,
            freeShippingThreshold,
            freeShippingThreshold
          );

          return;
        }

        const remaining =
          freeShippingThreshold -
          totalItems;

        shippingStatus.dataset.state =
          totalItems > 0
            ? "warning"
            : "neutral";

        shippingStatusText.textContent =
          totalItems === 0
            ? `Add quantities to check free-shipping eligibility.`
            : `Add ${number.format(
                remaining
              )} more ${itemWord(
                remaining
              )} to qualify for free shipping. ` +
              `Otherwise, shipping will be calculated after ` +
              `the final order is submitted.`;

        shippingProgressValue.textContent =
          `${number.format(
            totalItems
          )} / ${number.format(
            freeShippingThreshold
          )}`;

        setProgress(
          shippingProgress,
          shippingProgressBar,
          totalItems,
          freeShippingThreshold
        );
      }

      function updateCreditMilestone(
        totalItems,
        creditCount
      ) {
        if (
          !creditStatus ||
          !creditStatusText
        ) {
          return;
        }

        const progressItems =
          totalItems %
          creditThreshold;

        const remaining =
          progressItems === 0 &&
          totalItems > 0
            ? creditThreshold
            : creditThreshold -
              progressItems;

        creditStatus.dataset.state =
          creditCount > 0
            ? "success"
            : "neutral";

        if (totalItems === 0) {
          creditStatusText.textContent =
            `Add quantities to work toward your first ` +
            `${formatMoney(
              creditAmountCents
            )} order credit.`;
        } else if (
          creditCount === 0
        ) {
          creditStatusText.textContent =
            `Add ${number.format(
              remaining
            )} more ${itemWord(
              remaining
            )} to earn your first ` +
            `${formatMoney(
              creditAmountCents
            )} order credit.`;
        } else {
          creditStatusText.textContent =
            `${number.format(
              creditCount
            )} ${creditWord(
              creditCount
            )} earned. Add ` +
            `${number.format(
              remaining
            )} more ${itemWord(
              remaining
            )} to earn another ` +
            `${formatMoney(
              creditAmountCents
            )} credit.`;
        }

        creditProgressValue.textContent =
          `${number.format(
            progressItems
          )} / ${number.format(
            creditThreshold
          )}`;

        setProgress(
          creditProgress,
          creditProgressBar,
          progressItems,
          creditThreshold
        );
      }

      function update() {
        let totalItems = 0;
        let supporterSalesCents = 0;
        let productCostCents = 0;

        rows.forEach((row) => {
          const quantityInput =
            row.querySelector(
              "[data-oocc-calculator-quantity]"
            );

          const rowProfitOutput =
            row.querySelector(
              "[data-oocc-calculator-row-profit]"
            );

          const quantity =
            sanitizeQuantity(
              quantityInput
            );

          const unitCostCents =
            Math.max(
              0,
              integer(
                row.dataset
                  .unitCostCents,
                0
              )
            );

          const fundraisingPriceCents =
            Math.max(
              0,
              integer(
                row.dataset
                  .fundraisingPriceCents,
                0
              )
            );

          const rowSalesCents =
            quantity *
            fundraisingPriceCents;

          const rowCostCents =
            quantity *
            unitCostCents;

          const rowProfitCents =
            rowSalesCents -
            rowCostCents;

          totalItems += quantity;

          supporterSalesCents +=
            rowSalesCents;

          productCostCents +=
            rowCostCents;

          if (rowProfitOutput) {
            rowProfitOutput.textContent =
              formatMoney(
                rowProfitCents
              );
          }
        });

        const fulfillment =
          selectedFulfillment();

        const creditCount =
          Math.floor(
            totalItems /
            creditThreshold
          );

        const creditTotalCents =
          creditCount *
          creditAmountCents;

        const pickupTotalCents =
          fulfillment === "pickup"
            ? totalItems *
              pickupSavingsCents
            : 0;

        const invoiceCents =
          Math.max(
            0,
            productCostCents -
              creditTotalCents -
              pickupTotalCents
          );

        const fundsRaisedCents =
          Math.max(
            0,
            supporterSalesCents -
              invoiceCents
          );

        const freeShipping =
          fulfillment ===
            "shipping" &&
          totalItems >=
            freeShippingThreshold;

        const shippingCalculatedLater =
          fulfillment ===
            "shipping" &&
          totalItems <
            freeShippingThreshold;

        totalItemsOutput.textContent =
          number.format(
            totalItems
          );

        supporterSalesOutput.textContent =
          formatMoney(
            supporterSalesCents
          );

        creditCountOutput.textContent =
          number.format(
            creditCount
          );

        productCostOutput.textContent =
          formatMoney(
            productCostCents
          );

        creditTotalOutput.textContent =
          formatDeduction(
            creditTotalCents
          );

        if (pickupTotalOutput) {
          pickupTotalOutput.textContent =
            formatDeduction(
              pickupTotalCents
            );
        }

        invoiceOutput.textContent =
          formatMoney(
            invoiceCents
          );

        fundsRaisedOutput.textContent =
          formatWholeDollars(
            fundsRaisedCents
          );

        if (
          fulfillment ===
          "pickup"
        ) {
          shippingValueOutput.textContent =
            "Pickup";

          shippingDetailOutput.textContent =
            `No shipping charge. ` +
            `${formatMoney(
              pickupSavingsCents
            )} saved per item.`;

          invoiceNoteOutput.textContent =
            "Pickup savings are included in this estimate.";

          fundsQualifierOutput.textContent =
            "after projected credits and pickup savings";
        } else if (
          freeShipping
        ) {
          shippingValueOutput.textContent =
            "Free";

          shippingDetailOutput.textContent =
            "Free shipping to one organization location.";

          invoiceNoteOutput.textContent =
            "Free shipping is included in this estimate.";

          fundsQualifierOutput.textContent =
            "after projected credits";
        } else {
          shippingValueOutput.textContent =
            "Calculated later";

          shippingDetailOutput.textContent =
            `Orders under ${number.format(
              freeShippingThreshold
            )} items have shipping added to the finalized invoice.`;

          invoiceNoteOutput.textContent =
            "Shipping is not included in this estimate.";

          fundsQualifierOutput.textContent =
            totalItems === 0
              ? "Enter quantities to begin"
              : "estimated before shipping";
        }

        if (shippingOptionOutput) {
          if (
            totalItems >=
            freeShippingThreshold
          ) {
            shippingOptionOutput.textContent =
              "Free shipping unlocked for this projected order.";
          } else {
            const remaining =
              freeShippingThreshold -
              totalItems;

            shippingOptionOutput.textContent =
              totalItems === 0
                ? `Free with ${number.format(
                    freeShippingThreshold
                  )} or more total items.`
                : `Add ${number.format(
                    remaining
                  )} more ${itemWord(
                    remaining
                  )} for free shipping.`;
          }
        }

        if (totalItems === 0) {
          summarySentenceOutput.textContent =
            "Build a projected campaign using the quantities above.";
        } else if (
          shippingCalculatedLater
        ) {
          summarySentenceOutput.textContent =
            `Collect approximately ` +
            `${formatMoney(
              supporterSalesCents
            )} from supporters, pay an estimated product invoice of ` +
            `${formatMoney(
              invoiceCents
            )} to OOCC, and retain about ` +
            `${formatMoney(
              fundsRaisedCents
            )} before shipping.`;
        } else {
          summarySentenceOutput.textContent =
            `Collect approximately ` +
            `${formatMoney(
              supporterSalesCents
            )} from supporters, pay an estimated ` +
            `${formatMoney(
              invoiceCents
            )} to OOCC, and retain about ` +
            `${formatMoney(
              fundsRaisedCents
            )}.`;
        }

        updateShippingMilestone(
          totalItems,
          fulfillment
        );

        updateCreditMilestone(
          totalItems,
          creditCount
        );
      }

      rows.forEach((row) => {
        const quantityInput =
          row.querySelector(
            "[data-oocc-calculator-quantity]"
          );

        const decreaseButton =
          row.querySelector(
            "[data-oocc-calculator-decrease]"
          );

        const increaseButton =
          row.querySelector(
            "[data-oocc-calculator-increase]"
          );

        on(
          quantityInput,
          "input",
          update
        );

        on(
          quantityInput,
          "change",
          update
        );

        on(
          decreaseButton,
          "click",
          () => {
            const currentValue =
              sanitizeQuantity(
                quantityInput
              );

            quantityInput.value =
              String(
                Math.max(
                  0,
                  currentValue -
                    quantityStep
                )
              );

            update();
          }
        );

        on(
          increaseButton,
          "click",
          () => {
            const currentValue =
              sanitizeQuantity(
                quantityInput
              );

            quantityInput.value =
              String(
                currentValue +
                  quantityStep
              );

            update();
          }
        );
      });

      fulfillmentInputs.forEach(
        (input) => {
          on(
            input,
            "change",
            update
          );
        }
      );

      on(
        clearButton,
        "click",
        () => {
          rows.forEach((row) => {
            const input =
              row.querySelector(
                "[data-oocc-calculator-quantity]"
              );

            input.value = "0";
          });

          const defaultInput =
            fulfillmentInputs.find(
              (input) =>
                input.value ===
                defaultFulfillment
            );

          if (defaultInput) {
            defaultInput.checked =
              true;
          }

          update();
        }
      );

      update();

      return () => {
        listeners.forEach(
          (removeListener) => {
            removeListener();
          }
        );
      };
    }
  );


  /* ========================================================
     INTERNAL ANCHOR LINKS
     ======================================================== */

  function handleAnchorClick(event) {
    const link =
      event.target.closest(
        'a[href^="#"]'
      );

    if (!link) {
      return;
    }

    const href =
      link.getAttribute("href");

    if (!href || href === "#") {
      return;
    }

    let target = null;

    try {
      target =
        document.querySelector(
          href
        );
    } catch (error) {
      return;
    }

    if (!target) {
      return;
    }

    event.preventDefault();

    target.scrollIntoView({
      behavior:
        reducedMotionQuery.matches
          ? "auto"
          : "smooth",
      block: "start"
    });
  }


  /* ========================================================
     STARTUP AND THEME EDITOR
     ======================================================== */

  function start() {
    if (!isFundraisingPage()) {
      return;
    }

    document.addEventListener(
      "click",
      handleAnchorClick
    );

    document.addEventListener(
      "shopify:section:load",
      (event) => {
        initializeScope(
          event.target
        );
      }
    );

    document.addEventListener(
      "shopify:section:unload",
      (event) => {
        destroyScope(
          event.target
        );
      }
    );

    initializeScope(document);
  }

  window.OOCCFundraising = {
    register,
    initialize:
      initializeScope,
    destroy:
      destroyScope,
    integer,
    currencyFormatter,
    numberFormatter
  };

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      start,
      {
        once: true
      }
    );
  } else {
    start();
  }
})();

(() => {
  "use strict";

  const api =
    window.OOCCFundraising;

  if (!api) {
    return;
  }

  api.register(
    "[data-oocc-fundraising-application]",
    (section) => {
      const form =
        section.querySelector("form");

      if (!form) {
        return null;
      }

      const fulfillmentInputs =
        Array.from(
          section.querySelectorAll(
            "[data-oocc-application-fulfillment]"
          )
        );

      const shippingPanel =
        section.querySelector(
          "[data-oocc-application-shipping]"
        );

      const shippingFields =
        Array.from(
          section.querySelectorAll(
            "[data-oocc-application-shipping-field]"
          )
        );

      const campaignStart =
        section.querySelector(
          "[data-oocc-campaign-start]"
        );

      const campaignEnd =
        section.querySelector(
          "[data-oocc-campaign-end]"
        );

      const submitButton =
        section.querySelector(
          "[data-oocc-application-submit]"
        );

      const submitLabel =
        section.querySelector(
          "[data-oocc-application-submit-label]"
        );

      const successMessage =
        section.querySelector(
          "[data-oocc-application-success]"
        );

      const errorMessage =
        section.querySelector(
          "[data-oocc-application-errors]"
        );

      const finalDeadline =
        section.dataset
          .finalOrderDeadline || "";

      const listeners = [];


      /* ====================================================
         EVENT HELPERS
         ==================================================== */

      function on(
        element,
        eventName,
        handler,
        options
      ) {
        if (!element) {
          return;
        }

        element.addEventListener(
          eventName,
          handler,
          options
        );

        listeners.push(() => {
          element.removeEventListener(
            eventName,
            handler,
            options
          );
        });
      }


      /* ====================================================
         FULFILLMENT
         ==================================================== */

      function selectedFulfillment() {
        const selected =
          fulfillmentInputs.find(
            (input) =>
              input.checked
          );

        return selected
          ? selected.dataset
              .fulfillmentValue
          : "shipping";
      }

      function updateFulfillment() {
        if (!shippingPanel) {
          return;
        }

        const shippingSelected =
          selectedFulfillment() ===
          "shipping";

        shippingPanel.hidden =
          !shippingSelected;

        shippingPanel.setAttribute(
          "aria-hidden",
          String(!shippingSelected)
        );

        shippingFields.forEach(
          (field) => {
            field.disabled =
              !shippingSelected;

            if (
              field.hasAttribute(
                "data-required-for-shipping"
              )
            ) {
              field.required =
                shippingSelected;
            }

            if (!shippingSelected) {
              field.removeAttribute(
                "aria-invalid"
              );

              field.setCustomValidity(
                ""
              );
            }
          }
        );
      }


      /* ====================================================
         CAMPAIGN DATE VALIDATION
         ==================================================== */

      function validateDates() {
        if (
          !campaignStart ||
          !campaignEnd
        ) {
          return true;
        }

        campaignStart.setCustomValidity(
          ""
        );

        campaignEnd.setCustomValidity(
          ""
        );

        if (finalDeadline) {
          campaignStart.max =
            finalDeadline;

          campaignEnd.max =
            finalDeadline;
        }

        if (campaignStart.value) {
          campaignEnd.min =
            campaignStart.value;
        } else {
          campaignEnd.removeAttribute(
            "min"
          );
        }

        if (
          campaignStart.value &&
          campaignEnd.value &&
          campaignEnd.value <
            campaignStart.value
        ) {
          campaignEnd.setCustomValidity(
            "The selling end date must be on or after the campaign start date."
          );

          return false;
        }

        if (
          finalDeadline &&
          campaignEnd.value &&
          campaignEnd.value >
            finalDeadline
        ) {
          campaignEnd.setCustomValidity(
            "The selling end date must be before the final organization order deadline."
          );

          return false;
        }

        return true;
      }


      /* ====================================================
         VALIDATION PRESENTATION
         ==================================================== */

      function handleInvalid(event) {
        const field =
          event.target;

        if (
          field instanceof
          HTMLElement
        ) {
          field.setAttribute(
            "aria-invalid",
            "true"
          );
        }
      }

      function handleFieldInput(event) {
        const field =
          event.target;

        if (
          !(
            field instanceof
            HTMLInputElement
          ) &&
          !(
            field instanceof
            HTMLSelectElement
          ) &&
          !(
            field instanceof
            HTMLTextAreaElement
          )
        ) {
          return;
        }

        if (
          field === campaignStart ||
          field === campaignEnd
        ) {
          validateDates();
        }

        if (field.validity.valid) {
          field.removeAttribute(
            "aria-invalid"
          );
        }
      }


      /* ====================================================
         SUBMISSION
         ==================================================== */

      function handleSubmit(event) {
        validateDates();

        if (!form.checkValidity()) {
          event.preventDefault();

          const firstInvalid =
            form.querySelector(
              ":invalid"
            );

          if (
            firstInvalid &&
            typeof firstInvalid.focus ===
              "function"
          ) {
            firstInvalid.focus();
          }

          return;
        }

        if (
          submitButton &&
          submitLabel
        ) {
          submitButton.disabled =
            true;

          submitButton.setAttribute(
            "aria-busy",
            "true"
          );

          submitLabel.textContent =
            submitButton.dataset
              .loadingLabel ||
            "Submitting Application";
        }
      }


      /* ====================================================
         INITIALIZATION
         ==================================================== */

      fulfillmentInputs.forEach(
        (input) => {
          on(
            input,
            "change",
            updateFulfillment
          );
        }
      );

      on(
        campaignStart,
        "change",
        validateDates
      );

      on(
        campaignEnd,
        "change",
        validateDates
      );

      on(
        form,
        "invalid",
        handleInvalid,
        true
      );

      on(
        form,
        "input",
        handleFieldInput
      );

      on(
        form,
        "change",
        handleFieldInput
      );

      on(
        form,
        "submit",
        handleSubmit
      );

      updateFulfillment();
      validateDates();

      const statusMessage =
        successMessage ||
        errorMessage;

      if (statusMessage) {
        window.requestAnimationFrame(
          () => {
            statusMessage.focus({
              preventScroll: true
            });
          }
        );
      }


      /* ====================================================
         CLEANUP
         ==================================================== */

      return () => {
        listeners.forEach(
          (removeListener) => {
            removeListener();
          }
        );
      };
    }
  );

  api.initialize(document);
})();

(() => {
  "use strict";

  const api = window.OOCCFundraising;

  if (!api || typeof api.register !== "function") {
    return;
  }

  api.register(
    "[data-oocc-fundraising-collection]",
    (section) => {
      const shareButton = section.querySelector(
        "[data-oocc-share-products]"
      );

      const shareDialog = section.querySelector(
        "[data-oocc-share-dialog]"
      );

      const shareStatus = section.querySelector(
        "[data-oocc-share-status]"
      );

      const dialogStatus = section.querySelector(
        "[data-oocc-share-dialog-status]"
      );

      const shareLinkField = section.querySelector(
        "[data-oocc-share-link-field]"
      );

      const emailLink = section.querySelector(
        "[data-oocc-share-email]"
      );

      const textLink = section.querySelector(
        "[data-oocc-share-text-message]"
      );

      const facebookLink = section.querySelector(
        "[data-oocc-share-facebook]"
      );

      const dialogs = Array.from(
        section.querySelectorAll(
          "dialog[data-oocc-share-dialog], dialog[data-oocc-quick-view]"
        )
      );

      const cleanupFunctions = [];
      let activeTrigger = null;

      function addListener(
        element,
        eventName,
        handler,
        options
      ) {
        if (!element) {
          return;
        }

        element.addEventListener(
          eventName,
          handler,
          options
        );

        cleanupFunctions.push(() => {
          element.removeEventListener(
            eventName,
            handler,
            options
          );
        });
      }

      function absoluteUrl(value) {
        try {
          return new URL(
            value,
            window.location.origin
          ).href;
        } catch (error) {
          return window.location.href;
        }
      }

      function getShareData() {
        return {
          title:
            shareButton?.dataset.shareTitle ||
            document.title,

          text:
            shareButton?.dataset.shareText ||
            "",

          url: absoluteUrl(
            shareButton?.dataset.shareUrl ||
            window.location.href
          )
        };
      }

      function setShareStatus(message) {
        if (shareStatus) {
          shareStatus.textContent = message;
        }
      }

      function setDialogStatus(message) {
        if (dialogStatus) {
          dialogStatus.textContent = message;
        }
      }

      function copyText(text) {
        if (
          navigator.clipboard &&
          window.isSecureContext
        ) {
          return navigator.clipboard.writeText(text);
        }

        return new Promise(
          (resolve, reject) => {
            const textarea =
              document.createElement("textarea");

            textarea.value = text;
            textarea.setAttribute(
              "readonly",
              ""
            );

            textarea.style.position = "fixed";
            textarea.style.top = "0";
            textarea.style.left = "0";
            textarea.style.opacity = "0";

            document.body.appendChild(textarea);
            textarea.select();

            const copied =
              document.execCommand("copy");

            textarea.remove();

            if (copied) {
              resolve();
            } else {
              reject(
                new Error("Copy failed")
              );
            }
          }
        );
      }

      function pageHasOpenDialog() {
        return dialogs.some(
          (dialog) =>
            dialog.hasAttribute("open")
        );
      }

      function updatePageLock() {
        document.documentElement.classList.toggle(
          "oocc-fr-dialog-open",
          pageHasOpenDialog()
        );
      }

      function closeDialog(dialog) {
        if (!dialog) {
          return;
        }

        if (
          typeof dialog.close === "function" &&
          dialog.open
        ) {
          dialog.close();
        } else {
          dialog.removeAttribute("open");
          updatePageLock();

          if (
            activeTrigger &&
            document.contains(activeTrigger)
          ) {
            activeTrigger.focus();
          }
        }
      }

      function closeOtherDialogs(
        currentDialog
      ) {
        dialogs.forEach((dialog) => {
          if (
            dialog !== currentDialog &&
            dialog.hasAttribute("open")
          ) {
            if (
              typeof dialog.close ===
                "function" &&
              dialog.open
            ) {
              dialog.close();
            } else {
              dialog.removeAttribute("open");
            }
          }
        });
      }

      function openDialog(
        dialog,
        trigger
      ) {
        if (!dialog) {
          setShareStatus(
            "Sharing options are temporarily unavailable."
          );

          return;
        }

        closeOtherDialogs(dialog);
        activeTrigger = trigger || null;

        if (
          typeof dialog.showModal ===
            "function"
        ) {
          if (!dialog.open) {
            dialog.showModal();
          }
        } else {
          dialog.setAttribute("open", "");
        }

        updatePageLock();
      }

      function prepareShareLinks() {
        const shareData = getShareData();

        const message = [
          shareData.text,
          shareData.url
        ]
          .filter(Boolean)
          .join("\n\n");

        if (shareLinkField) {
          shareLinkField.value =
            shareData.url;
        }

        if (emailLink) {
          emailLink.href =
            `mailto:?subject=${encodeURIComponent(
              shareData.title
            )}&body=${encodeURIComponent(
              message
            )}`;
        }

        if (textLink) {
          textLink.href =
            `sms:?&body=${encodeURIComponent(
              message
            )}`;
        }

        if (facebookLink) {
          facebookLink.href =
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
              shareData.url
            )}`;
        }
      }

      function openShareWindow(
        trigger
      ) {
        prepareShareLinks();
        setShareStatus("");
        setDialogStatus("");

        openDialog(
          shareDialog,
          trigger
        );
      }

      async function copyShareLink() {
        const shareData = getShareData();

        try {
          await copyText(shareData.url);

          setShareStatus(
            "Fundraising product link copied."
          );

          setDialogStatus(
            "Link copied. Paste it into a text, email or social post."
          );
        } catch (error) {
          setDialogStatus(
            "Select the link above and copy it manually."
          );
        }
      }

      function handleSectionClick(event) {
        const target =
          event.target instanceof Element
            ? event.target
            : null;

        if (!target) {
          return;
        }

        const shareTrigger = target.closest(
          "[data-oocc-share-products]"
        );

        if (
          shareTrigger &&
          section.contains(shareTrigger)
        ) {
          event.preventDefault();

          openShareWindow(shareTrigger);
          return;
        }

        const quickViewTrigger =
          target.closest(
            "[data-oocc-quick-view-open]"
          );

        if (
          quickViewTrigger &&
          section.contains(
            quickViewTrigger
          )
        ) {
          event.preventDefault();

          const dialogId =
            quickViewTrigger.dataset
              .ooccQuickViewOpen;

          const dialog =
            document.getElementById(
              dialogId
            );

          openDialog(
            dialog,
            quickViewTrigger
          );

          return;
        }

        const copyButton = target.closest(
          "[data-oocc-share-copy]"
        );

        if (
          copyButton &&
          section.contains(copyButton)
        ) {
          event.preventDefault();

          copyShareLink();
          return;
        }

        const closeButton = target.closest(
          "[data-oocc-dialog-close]"
        );

        if (
          closeButton &&
          section.contains(closeButton)
        ) {
          event.preventDefault();

          closeDialog(
            closeButton.closest("dialog")
          );

          return;
        }

        const shareDestination =
          target.closest(
            "[data-oocc-share-destination]"
          );

        if (
          shareDestination &&
          section.contains(
            shareDestination
          )
        ) {
          window.setTimeout(() => {
            closeDialog(
              shareDestination.closest(
                "dialog"
              )
            );
          }, 150);
        }
      }

      function handleDialogBackdropClick(
        event
      ) {
        const dialog = event.currentTarget;

        if (event.target !== dialog) {
          return;
        }

        const bounds =
          dialog.getBoundingClientRect();

        const clickedInside =
          event.clientX >= bounds.left &&
          event.clientX <= bounds.right &&
          event.clientY >= bounds.top &&
          event.clientY <= bounds.bottom;

        if (!clickedInside) {
          closeDialog(dialog);
        }
      }

      function handleDialogClose() {
        updatePageLock();

        if (
          activeTrigger &&
          document.contains(activeTrigger)
        ) {
          activeTrigger.focus();
        }
      }

      dialogs.forEach((dialog) => {
        addListener(
          dialog,
          "click",
          handleDialogBackdropClick
        );

        addListener(
          dialog,
          "close",
          handleDialogClose
        );

        addListener(
          dialog,
          "cancel",
          () => {
            window.setTimeout(
              updatePageLock,
              0
            );
          }
        );
      });

      addListener(
        section,
        "click",
        handleSectionClick
      );

      prepareShareLinks();

      return () => {
        cleanupFunctions.forEach(
          (cleanup) => cleanup()
        );

        document.documentElement.classList.remove(
          "oocc-fr-dialog-open"
        );
      };
    }
  );

  api.initialize(document);
})();

(() => {
  "use strict";

  const api =
    window.OOCCFundraising;

  if (
    !api ||
    typeof api.register !== "function"
  ) {
    return;
  }

  api.register(
    "[data-oocc-fundraising-product-guide]",
    (section) => {
      const cards = Array.from(
        section.querySelectorAll(
          ".oocc-fr-product-guide__card"
        )
      );

      if (!cards.length) {
        return null;
      }

      /* Remove remnants if the section reloads
         inside the Shopify Theme Editor. */

      section
        .querySelectorAll(
          "[data-oocc-pg-open]"
        )
        .forEach((element) => {
          element.remove();
        });

      section
        .querySelectorAll(
          "[data-oocc-pg-modal]"
        )
        .forEach((element) => {
          element.remove();
        });


      /* Create one reusable dialog. */

      const sectionWrapper =
        section.closest(
          ".shopify-section"
        );

      const sectionKey = (
        sectionWrapper?.id ||
        "oocc-product-guide"
      ).replace(
        /[^a-zA-Z0-9_-]/g,
        ""
      );

      const modalId =
        `OOCCProductGuideModal-${sectionKey}`;

      const titleId =
        `${modalId}-Title`;

      const dialog =
        document.createElement(
          "dialog"
        );

      dialog.id = modalId;
      dialog.className =
        "oocc-fr-pg-modal";

      dialog.setAttribute(
        "aria-labelledby",
        titleId
      );

      dialog.setAttribute(
        "data-oocc-pg-modal",
        ""
      );

      dialog.innerHTML = `
        <div class="oocc-fr-pg-modal__shell">
          <button
            type="button"
            class="oocc-fr-pg-modal__close"
            aria-label="Close product details"
            data-oocc-pg-close
          >
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M6 6l12 12"></path>
              <path d="M18 6 6 18"></path>
            </svg>
          </button>

          <div class="oocc-fr-pg-modal__media">
            <div class="oocc-fr-pg-modal__image-wrap">
              <img
                class="oocc-fr-pg-modal__image"
                alt=""
                data-oocc-pg-modal-image
                hidden
              >
            </div>
          </div>

          <div class="oocc-fr-pg-modal__content">
            <div class="oocc-fr-pg-modal__content-inner">
              <p class="oocc-fr-eyebrow">
                FUNDRAISING SELECTION
              </p>

              <h2
                id="${titleId}"
                class="oocc-fr-pg-modal__title"
                data-oocc-pg-modal-title
              ></h2>

              <p
                class="oocc-fr-pg-modal__format"
                data-oocc-pg-modal-format
              ></p>

              <p class="oocc-fr-pg-modal__price">
                <strong
                  data-oocc-pg-modal-price
                ></strong>

                <span>
                  Suggested fundraising price*
                </span>
              </p>

              <p
                class="oocc-fr-pg-modal__description"
                data-oocc-pg-modal-description
              ></p>

              <div
                class="oocc-fr-pg-modal__notes"
                data-oocc-pg-modal-notes-wrap
              >
                <span>Fragrance notes</span>

                <p
                  data-oocc-pg-modal-notes
                ></p>
              </div>

              <div class="oocc-fr-pg-modal__order">
                <span
                  class="oocc-fr-pg-modal__order-icon"
                  aria-hidden="true"
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M5 6h14l-1.2 9H7z"></path>
                    <path d="M8 6 9.5 3.5"></path>
                    <path d="M16 6 14.5 3.5"></path>
                    <circle cx="9" cy="19" r="1.5"></circle>
                    <circle cx="16" cy="19" r="1.5"></circle>
                  </svg>
                </span>

                <div>
                  <strong>How to order</strong>

                  <p>
                    Place your order directly with the seller or
                    organization running the fundraiser. The complete
                    campaign order is delivered to the campaign
                    coordinator for supporter distribution.
                  </p>
                </div>
              </div>

              <button
                type="button"
                class="oocc-fr-button oocc-fr-button--primary oocc-fr-pg-modal__done"
                data-oocc-pg-close
              >
                Back to Products
              </button>
            </div>
          </div>
        </div>
      `;

      section.appendChild(dialog);


      /* Dialog elements */

      const modalImage =
        dialog.querySelector(
          "[data-oocc-pg-modal-image]"
        );

      const modalTitle =
        dialog.querySelector(
          "[data-oocc-pg-modal-title]"
        );

      const modalFormat =
        dialog.querySelector(
          "[data-oocc-pg-modal-format]"
        );

      const modalPrice =
        dialog.querySelector(
          "[data-oocc-pg-modal-price]"
        );

      const modalDescription =
        dialog.querySelector(
          "[data-oocc-pg-modal-description]"
        );

      const modalNotesWrap =
        dialog.querySelector(
          "[data-oocc-pg-modal-notes-wrap]"
        );

      const modalNotes =
        dialog.querySelector(
          "[data-oocc-pg-modal-notes]"
        );

      const cleanupFunctions = [];
      const triggers = [];

      let activeTrigger = null;


      /* Event helper */

      function on(
        element,
        eventName,
        handler,
        options
      ) {
        if (!element) {
          return;
        }

        element.addEventListener(
          eventName,
          handler,
          options
        );

        cleanupFunctions.push(() => {
          element.removeEventListener(
            eventName,
            handler,
            options
          );
        });
      }


      /* Pull the largest existing responsive
         image candidate from Shopify's srcset. */

      function largestImageUrl(image) {
        if (!image) {
          return "";
        }

        const srcset =
          image.getAttribute(
            "srcset"
          );

        if (!srcset) {
          return (
            image.currentSrc ||
            image.src ||
            ""
          );
        }

        const candidates = srcset
          .split(",")
          .map((candidate) => {
            const parts =
              candidate
                .trim()
                .split(/\s+/);

            return {
              url: parts[0] || "",
              width:
                Number.parseInt(
                  parts[1],
                  10
                ) || 0
            };
          })
          .filter((candidate) => {
            return Boolean(
              candidate.url
            );
          })
          .sort((first, second) => {
            return (
              first.width -
              second.width
            );
          });

        const largest =
          candidates[
            candidates.length - 1
          ];

        return (
          largest?.url ||
          image.currentSrc ||
          image.src ||
          ""
        );
      }


      function textFrom(
        card,
        selector
      ) {
        return (
          card
            .querySelector(selector)
            ?.textContent
            ?.trim() ||
          ""
        );
      }


      function populateDialog(card) {
        const productImage =
          card.querySelector(
            ".oocc-fr-product-guide__image"
          );

        const imageUrl =
          largestImageUrl(
            productImage
          );

        const title =
          textFrom(
            card,
            "h3"
          );

        const format =
          textFrom(
            card,
            ".oocc-fr-product-guide__format"
          );

        const price =
          textFrom(
            card,
            ".oocc-fr-product-guide__price strong"
          );

        const description =
          textFrom(
            card,
            ".oocc-fr-product-guide__description"
          );

        const notes =
          textFrom(
            card,
            ".oocc-fr-product-guide__notes p"
          );

        if (modalTitle) {
          modalTitle.textContent =
            title;
        }

        if (modalFormat) {
          modalFormat.textContent =
            format;
        }

        if (modalPrice) {
          modalPrice.textContent =
            price;
        }

        if (modalDescription) {
          modalDescription.textContent =
            description;

          modalDescription.hidden =
            !description;
        }

        if (
          modalNotesWrap &&
          modalNotes
        ) {
          modalNotes.textContent =
            notes;

          modalNotesWrap.hidden =
            !notes;
        }

        if (modalImage) {
          if (imageUrl) {
            modalImage.src =
              imageUrl;

            modalImage.alt =
              productImage?.alt ||
              title;

            modalImage.hidden =
              false;
          } else {
            modalImage.removeAttribute(
              "src"
            );

            modalImage.alt = "";
            modalImage.hidden = true;
          }
        }
      }


      function openDialog(
        card,
        trigger
      ) {
        populateDialog(card);

        activeTrigger =
          trigger || null;

        if (
          typeof dialog.showModal ===
            "function"
        ) {
          if (!dialog.open) {
            dialog.showModal();
          }
        } else {
          dialog.setAttribute(
            "open",
            ""
          );
        }

        document.documentElement
          .classList.add(
            "oocc-fr-dialog-open"
          );
      }


      function closeDialog() {
        if (
          typeof dialog.close ===
            "function" &&
          dialog.open
        ) {
          dialog.close();
        } else {
          dialog.removeAttribute(
            "open"
          );

          document.documentElement
            .classList.remove(
              "oocc-fr-dialog-open"
            );

          if (
            activeTrigger &&
            document.contains(
              activeTrigger
            )
          ) {
            activeTrigger.focus();
          }
        }
      }


      /* Add an accessible image trigger
         to each existing product card. */

      cards.forEach((card) => {
        const imageWrap =
          card.querySelector(
            ".oocc-fr-product-guide__image-wrap"
          );

        if (!imageWrap) {
          return;
        }

        const productTitle =
          textFrom(
            card,
            "h3"
          );

        const trigger =
          document.createElement(
            "button"
          );

        trigger.type = "button";

        trigger.className =
          "oocc-fr-product-guide__popup-trigger";

        trigger.setAttribute(
          "data-oocc-pg-open",
          ""
        );

        trigger.setAttribute(
          "aria-haspopup",
          "dialog"
        );

        trigger.setAttribute(
          "aria-controls",
          modalId
        );

        trigger.setAttribute(
          "aria-label",
          `View larger image and details for ${productTitle}`
        );

        trigger.innerHTML = `
          <span>
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <circle
                cx="10.5"
                cy="10.5"
                r="6.5"
              ></circle>

              <path d="m15.5 15.5 4 4"></path>
              <path d="M10.5 7.5v6"></path>
              <path d="M7.5 10.5h6"></path>
            </svg>

            View Product
          </span>
        `;

        imageWrap.appendChild(
          trigger
        );

        triggers.push(trigger);

        on(
          trigger,
          "click",
          () => {
            openDialog(
              card,
              trigger
            );
          }
        );
      });


      /* Closing controls */

      on(
        dialog,
        "click",
        (event) => {
          const target =
            event.target instanceof
            Element
              ? event.target
              : null;

          if (
            target?.closest(
              "[data-oocc-pg-close]"
            )
          ) {
            closeDialog();
            return;
          }

          if (
            event.target === dialog
          ) {
            closeDialog();
          }
        }
      );

      on(
        dialog,
        "close",
        () => {
          document.documentElement
            .classList.remove(
              "oocc-fr-dialog-open"
            );

          if (
            activeTrigger &&
            document.contains(
              activeTrigger
            )
          ) {
            activeTrigger.focus();
          }
        }
      );

      on(
        dialog,
        "cancel",
        () => {
          window.setTimeout(
            () => {
              document.documentElement
                .classList.remove(
                  "oocc-fr-dialog-open"
                );
            },
            0
          );
        }
      );


      /* Theme-editor cleanup */

      return () => {
        cleanupFunctions.forEach(
          (removeListener) => {
            removeListener();
          }
        );

        triggers.forEach(
          (trigger) => {
            trigger.remove();
          }
        );

        dialog.remove();

        document.documentElement
          .classList.remove(
            "oocc-fr-dialog-open"
          );
      };
    }
  );

  api.initialize(document);
})();